import { useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateFamilyMember } from '@/hooks/useFamilyMembers';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { PersonAutocomplete } from './PersonAutocomplete';

// ─── Eltern zuordnen ─────────────────────────────────────────────────────────
//
// Das Gegenstück zu „Kind hinzufügen". Beide schreiben denselben Datensatz –
// Kind, Elternteil, Elternteil –, nur von der anderen Seite aus.
//
// Warum es beide braucht: Ahnenforschung läuft rückwärts. Man trägt sich selbst
// ein und sucht dann die Eltern, die Großeltern, die Urgroßeltern. Bisher ging
// nur der Weg vorwärts – wer seine Mutter eintragen wollte, musste erst sie
// anlegen, dann zu ihr wechseln und sich selbst als Kind zuordnen. Das
// Datenmodell konnte es die ganze Zeit; es fehlte nur die Tür auf dieser Seite.
//
// Ringschlüsse fängt der Server ab (er läuft die Vorfahren aufwärts ab und
// weist 409 zurück, wenn das Kind unter ihnen ist). Hier wird nur der
// offensichtliche Fall vorab ausgeblendet: die Person selbst.

const schema = z
  .object({
    parent1Id: z.string().optional(),
    parent2Id: z.string().optional(),
    relationType: z.enum(['biological', 'adopted', 'foster', 'step']),
  })
  // Ein Eintrag ohne jeden Elternteil wäre ein leerer Datensatz – und genau
  // der Fehler, den jemand macht, der das Formular versehentlich abschickt.
  .refine((v) => Boolean(v.parent1Id) || Boolean(v.parent2Id), {
    message: 'Bitte mindestens einen Elternteil wählen.',
    path: ['parent1Id'],
  });

type Werte = z.infer<typeof schema>;

const ELTERN_JE_ART: Record<Werte['relationType'], string> = {
  biological: 'leibliche Eltern',
  adopted: 'Adoptiveltern',
  foster: 'Pflegeeltern',
  step: 'Stiefeltern',
};

const ART_OPTIONEN: Array<{ value: Werte['relationType']; label: string }> = [
  { value: 'biological', label: 'Biologisch' },
  { value: 'adopted', label: 'Adoptiert' },
  { value: 'foster', label: 'Pflegekind' },
  { value: 'step', label: 'Stiefkind' },
];

interface Props {
  /** Die Person, der Eltern zugeordnet werden. */
  childId: string;
  childName: string;
  /** Bereits zugeordnete Eltern – sie stehen nicht noch einmal zur Wahl. */
  existingParentIds?: string[];
  /** Die bestehenden Zuordnungen je Art, mit Namen – für den Hinweis unten. */
  bestehende?: Array<{ relationType: string; namen: string[] }>;
  onClose: () => void;
}

export function ElternForm({
  childId,
  childName,
  existingParentIds = [],
  bestehende = [],
  onClose,
}: Props) {
  const anlegen = useCreateFamilyMember('eltern');
  const confirm = useConfirm();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Werte>({
    resolver: zodResolver(schema),
    defaultValues: { parent1Id: '', parent2Id: '', relationType: 'biological' },
  });

  const parent1 = watch('parent1Id');
  const parent2 = watch('parent2Id');
  const art = watch('relationType');

  // Je Art höchstens zwei Eltern (der Server prüft dasselbe). Steht schon einer
  // da, wird der neue mit ihm zum Paar; stehen zwei da, ist kein Platz mehr.
  const schonDa = [...new Set(bestehende.filter((b) => b.relationType === art).flatMap((b) => b.namen))];
  const neuGewaehlt = [parent1, parent2].filter(Boolean).length;
  const voll = schonDa.length >= 2 || schonDa.length + neuGewaehlt > 2;

  const requestClose = useCallback(async () => {
    if (isSubmitting) return;
    if (isDirty) {
      const ok = await confirm({
        title: 'Eingaben verwerfen?',
        message: 'Nicht gespeicherte Änderungen gehen verloren.',
        confirmLabel: 'Verwerfen',
        cancelLabel: 'Weiter bearbeiten',
        variant: 'default',
      });
      if (!ok) return;
    }
    onClose();
  }, [confirm, isDirty, isSubmitting, onClose]);

  useEffect(() => {
    function esc(e: KeyboardEvent) {
      if (e.key === 'Escape') requestClose();
    }
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [requestClose]);

  const onSubmit = async (werte: Werte) => {
    try {
      await anlegen.mutateAsync({
        child_id: childId,
        // Wer nur einen Elternteil kennt, trägt einen ein. Steht nur der
        // zweite, rückt er auf Platz eins – ein Datensatz mit leerem
        // parent1_id und gefülltem parent2_id wäre eine Unterscheidung ohne
        // Unterschied.
        parent1_id: werte.parent1Id || werte.parent2Id || undefined,
        parent2_id: werte.parent1Id ? werte.parent2Id || undefined : undefined,
        relation_type: werte.relationType,
      });
      onClose();
    } catch {
      // Der Hook meldet den Fehler – auch den Ringschluss vom Server.
    }
  };

  const ausschliessen = [childId, ...existingParentIds];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={requestClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">Eltern zuordnen</h2>
            <p className="truncate text-xs text-slate-500">für {childName}</p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Schließen"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Erster Elternteil
            </label>
            <Controller
              name="parent1Id"
              control={control}
              render={({ field }) => (
                <PersonAutocomplete
                  value={field.value || null}
                  onChange={(id) => field.onChange(id ?? '')}
                  placeholder="Person suchen…"
                  excludeIds={[...ausschliessen, ...(parent2 ? [parent2] : [])]}
                  aria-label="Erster Elternteil"
                />
              )}
            />
            {errors.parent1Id && (
              <p className="mt-1 text-xs text-red-500">{errors.parent1Id.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Zweiter Elternteil <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <Controller
              name="parent2Id"
              control={control}
              render={({ field }) => (
                <PersonAutocomplete
                  value={field.value || null}
                  onChange={(id) => field.onChange(id ?? '')}
                  placeholder="Person suchen…"
                  excludeIds={[...ausschliessen, ...(parent1 ? [parent1] : [])]}
                  aria-label="Zweiter Elternteil"
                />
              )}
            />
            <p className="mt-1 text-xs text-slate-400">
              Beide zusammen bilden ein Elternpaar; die Abstammungslinie geht dann vom Paar
              aus. Wer nur einen Elternteil kennt, lässt das Feld leer.
            </p>
          </div>

          <div>
            <label htmlFor="ef-art" className="mb-1 block text-sm font-medium text-slate-700">
              Art der Zuordnung
            </label>
            <select
              id="ef-art"
              {...register('relationType')}
              className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              {ART_OPTIONEN.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {schonDa.length > 0 && (
            <p
              className={`rounded-md px-3 py-2 text-xs ${
                voll ? 'bg-amber-50 text-amber-800' : 'bg-slate-50 text-slate-600'
              }`}
            >
              {voll
                ? `Als ${ELTERN_JE_ART[art]} sind bereits ${schonDa.join(' und ')} eingetragen` +
                  (schonDa.length >= 2
                    ? ' – mehr als zwei sind nicht möglich. Zuerst die bestehende Zuordnung entfernen.'
                    : ' – es ist nur noch Platz für einen Elternteil.')
                : `${schonDa[0]} ist bereits eingetragen; der gewählte Elternteil wird mit ${schonDa[0]} zum Paar ergänzt.`}
            </p>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || voll}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Wird gespeichert…' : 'Zuordnen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
